<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AIAssistant from '@/components/AIAssistant.vue';

// Import custom icons
import documentIcon from '@/assets/icons/blue-document-icon.png';
import chatIcon from '@/assets/icons/blue-chat-icon.png';
import penIcon from '@/assets/icons/blue-pen-icon.png';
import magicIcon from '@/assets/icons/magic-blue-icon.png';

const router = useRouter();
const { t } = useI18n();

const aiAssistantRef = ref(null);

const handleCreateNewDocument = () => {
  router.push({ name: 'template-selection' });
};

const handleAIAssistant = () => {
  if (aiAssistantRef.value) {
    aiAssistantRef.value.openChat();
  }
};
</script>

<template>
  <div class="welcome-container">
    <div class="welcome-content">
      <h1 class="welcome-title">{{ t('welcome.title') }}</h1>
      <p class="welcome-description">
        {{ t('welcome.description') }}
      </p>

      <div class="steps-container">
        <div class="step-item">
          <div class="step-icon-wrapper">
            <img :src="documentIcon" alt="Document" class="step-icon" />
          </div>
          <h3 class="step-title">{{ t('welcome.step1_title') }}</h3>
          <p class="step-description">
            {{ t('welcome.step1_description') }}
          </p>
        </div>

        <div class="step-connector">
          <svg class="connector-line" viewBox="0 0 100 50" preserveAspectRatio="none">
            <path d="M0,25 Q50,5 100,25" stroke="#BEBEBE" stroke-width="2" fill="none" stroke-dasharray="5,5"/>
          </svg>
        </div>

        <div class="step-item">
          <div class="step-icon-wrapper">
            <img :src="chatIcon" alt="Chat" class="step-icon" />
          </div>
          <h3 class="step-title">{{ t('welcome.step2_title') }}</h3>
          <p class="step-description">
            {{ t('welcome.step2_description') }}
          </p>
        </div>

        <div class="step-connector">
          <svg class="connector-line" viewBox="0 0 100 50" preserveAspectRatio="none">
            <path d="M0,25 Q50,45 100,25" stroke="#BEBEBE" stroke-width="2" fill="none" stroke-dasharray="5,5"/>
          </svg>
        </div>

        <div class="step-item">
          <div class="step-icon-wrapper">
            <img :src="penIcon" alt="Pen" class="step-icon" />
          </div>
          <h3 class="step-title">{{ t('welcome.step3_title') }}</h3>
          <p class="step-description">
            {{ t('welcome.step3_description') }}
          </p>
        </div>
      </div>

      <div class="action-button-container">
        <v-btn
          size="large"
          @click="handleCreateNewDocument"
          class="create-document-btn"
          flat
        >
          {{ t('welcome.create_new_document') }}
        </v-btn>
      </div>
    </div>

    <!-- AI Assistant Button -->
    <div class="ai-assistant-button">
      <button @click="handleAIAssistant" class="ai-btn">
        <img :src="magicIcon" alt="AI" class="ai-icon" />
        <span>{{ t('templateSelection.askAI') }}</span>
      </button>
    </div>

    <!-- AI Assistant Component (hidden, controlled externally) -->
    <AIAssistant
      ref="aiAssistantRef"
      buttonStyle="external"
      :currentDocument="null"
    />
  </div>
</template>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;600;700&display=swap');

@keyframes subtleGlow {
  0%, 100% {
    box-shadow: 0 2px 12px rgba(0, 38, 239, 0.15);
  }
  50% {
    box-shadow: 0 2px 16px rgba(0, 38, 239, 0.25);
  }
}

@keyframes swordShine {
  0% {
    left: -150%;
  }
  100% {
    left: 150%;
  }
}

.welcome-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 64px);
  padding: 3rem 2rem;
  background: #F9F9F9;
  position: relative;
}

.welcome-content {
  max-width: 1200px;
  width: 100%;
  text-align: center;
  padding: 2rem;
}

.welcome-title {
  font-family: 'Archivo', sans-serif;
  font-size: 48px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: 0;
  text-align: center;
  text-transform: capitalize;
  color: #1D1D1D;
  margin-bottom: 1.5rem;
}

.welcome-description {
  font-family: 'Archivo', sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: 0;
  text-align: center;
  color: #636D7B;
  margin-bottom: 4rem;
  max-width: 850px;
  margin-left: auto;
  margin-right: auto;
}

.steps-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 0;
  margin-bottom: 4rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 1100px;
  flex-wrap: nowrap;
  position: relative;

  @media (max-width: 992px) {
    flex-direction: column;
    align-items: center;
    gap: 3rem;

    .step-connector {
      display: none;
    }
  }
}

.step-item {
  flex: 1;
  width: 300px;
  max-width: 300px;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.step-icon-wrapper {
  box-sizing: border-box;
  width: 100px;
  height: 100px;
  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  box-shadow: inset -4px 4px 30px rgba(255, 255, 255, 0.5),
              inset 4px 4px 30px #D1D9E6;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.step-icon {
  width: 50px;
  height: 50px;
  object-fit: contain;
}

.step-title {
  font-family: 'Archivo', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: 0;
  text-align: center;
  color: #1D1D1D;
  margin-bottom: 0.75rem;
}

.step-description {
  font-family: 'Archivo', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0;
  text-align: center;
  color: #636D7B;
}

.step-connector {
  width: 120px;
  height: 60px;
  position: relative;
  margin: 30px -10px 0;
  flex-shrink: 0;

  .connector-line {
    width: 100%;
    height: 100%;
  }
}

.action-button-container {
  margin-top: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.create-document-btn {
  width: 291px !important;
  height: 56px !important;
  background-color: #D8ED84 !important;
  color: #1D1D1D !important;
  font-family: 'Archivo', sans-serif !important;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 32px !important;
  letter-spacing: 0 !important;
  text-align: center !important;
  vertical-align: middle !important;
  padding: 0 40px !important;
  margin: 0 auto !important;
  gap: 8px !important;
  text-transform: none !important;
  border-radius: 100px !important;
  box-shadow: none !important;
  opacity: 1 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.2s ease !important;

  &:hover {
    background-color: #cfe37b !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  }

  &:active {
    transform: translateY(0) !important;
    background-color: #D8ED84 !important;
  }
}

.ai-assistant-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 100;
}

.ai-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 290px;
  height: 54px;
  gap: 8px;
  background: white;
  border: 1px solid rgba(0, 38, 239, 0.3);
  border-radius: 100px;
  padding: 16px 32px;
  font-family: 'Archivo', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #0026EF;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: subtleGlow 4s infinite ease-in-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -150%;
    width: 50px;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      rgba(255, 255, 255, 0.35),
      transparent
    );
    transform: skewX(-20deg);
    animation: swordShine 8s infinite;
    animation-delay: 1s;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 100px;
    padding: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 38, 239, 0.2), transparent);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: exclude;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: #0026EF;
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(0, 38, 239, 0.2);

    &::after {
      opacity: 1;
    }

    &::before {
      animation-duration: 6s;
    }

    .ai-icon {
      filter: brightness(1.1);
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(0, 38, 239, 0.15);
  }

  .ai-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;
  }

  span {
    white-space: nowrap;
    font-weight: 500;
    position: relative;
    z-index: 1;
  }
}


// Tablet - Large (1024px)
@media (max-width: 1024px) {
  .welcome-title {
    font-size: 42px;
  }

  .welcome-description {
    font-size: 18px;
    padding: 0 1rem;
  }

  .step-item {
    width: 280px;
    max-width: 280px;
    padding: 0 1rem;
  }

  .step-title {
    font-size: 18px;
  }

  .step-description {
    font-size: 15px;
  }

  .step-icon-wrapper {
    width: 90px;
    height: 90px;
  }

  .step-icon {
    width: 45px;
    height: 45px;
  }
}

// Tablet - Small (768px)
@media (max-width: 768px) {
  .welcome-container {
    padding: 2rem 1rem 7rem 1rem;
  }

  .welcome-title {
    font-size: 36px;
    line-height: 110%;
  }

  .welcome-description {
    font-size: 17px;
    line-height: 130%;
    margin-bottom: 3rem;
  }

  .steps-container {
    flex-direction: column;
    gap: 2rem;
    margin-bottom: 3rem;

    .step-connector {
      display: none;
    }
  }

  .step-item {
    width: 100%;
    max-width: 350px;
  }

  .step-title {
    font-size: 18px;
    margin-bottom: 0.5rem;
  }

  .step-description {
    font-size: 14px;
    line-height: 140%;
  }

  .step-icon-wrapper {
    width: 85px;
    height: 85px;
    margin-bottom: 1rem;
  }

  .step-icon {
    width: 42px;
    height: 42px;
  }

  .create-document-btn {
    width: 100% !important;
    max-width: 320px !important;
    font-size: 18px !important;
    height: 52px !important;
  }

  .ai-assistant-button {
    bottom: 6rem;
    right: 1rem;
    left: 1rem;
    display: flex;
    justify-content: center;
  }

  .ai-btn {
    width: calc(100% - 2rem);
    max-width: 290px;
    font-size: 15px;
    height: 50px;
    padding: 14px 28px;
  }
}

// Mobile - Large (480px)
@media (max-width: 480px) {
  .welcome-container {
    padding: 1.5rem 1rem 8rem 1rem;
    min-height: calc(100vh - 56px);
  }

  .welcome-content {
    padding: 1rem;
    padding-bottom: 4rem;
  }

  .welcome-title {
    font-size: 28px;
    margin-bottom: 1rem;
  }

  .welcome-description {
    font-size: 15px;
    line-height: 130%;
    margin-bottom: 2.5rem;
    padding: 0;
  }

  .steps-container {
    gap: 1.5rem;
  }

  .step-item {
    width: 100%;
    max-width: 100%;
    padding: 0;
  }

  .step-title {
    font-size: 16px;
  }

  .step-description {
    font-size: 13px;
    padding: 0 0.5rem;
  }

  .step-icon-wrapper {
    width: 75px;
    height: 75px;
    box-shadow: inset -3px 3px 20px rgba(255, 255, 255, 0.5),
                inset 3px 3px 20px #D1D9E6;
  }

  .step-icon {
    width: 38px;
    height: 38px;
  }

  .create-document-btn {
    width: 100% !important;
    font-size: 17px !important;
    height: 48px !important;
    padding: 0 30px !important;
  }

  .ai-btn {
    font-size: 14px;
    height: 46px;
    padding: 12px 24px;

    .ai-icon {
      width: 20px;
      height: 20px;
    }
  }

  .ai-assistant-button {
    bottom: 7rem;
  }
}

// Mobile - Small (360px)
@media (max-width: 360px) {
  .welcome-title {
    font-size: 24px;
  }

  .welcome-description {
    font-size: 14px;
  }

  .step-title {
    font-size: 15px;
  }

  .step-description {
    font-size: 12px;
  }

  .step-icon-wrapper {
    width: 70px;
    height: 70px;
  }

  .step-icon {
    width: 35px;
    height: 35px;
  }

  .create-document-btn {
    font-size: 16px !important;
    height: 46px !important;
  }
}
</style>